import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Section, GoldButton } from "@/components/site/Ui";
import { CheckCircle2, Loader2, ClipboardList } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <Section className="max-w-xl">
      <div className="card-surface rounded-3xl p-8 sm:p-12 text-center gold-glow">
        {status === "checking" && (
          <><Loader2 className="mx-auto text-[#D4AF37] animate-spin" size={44} />
            <h1 className="mt-5 font-display text-2xl">Confirming your payment…</h1>
            <p className="mt-2 text-white/60 text-sm">Just a moment, please don't close this page.</p></>
        )}
        {status === "paid" && info?.kind === "consultation" && (
          <>
            <CheckCircle2 className="mx-auto text-[#D4AF37]" size={52} />
            <h1 className="mt-5 font-display text-3xl">Payment received!</h1>
            <p className="mt-3 text-white/70">Thank you! Your 1:1 consultation <span className="text-[#D4AF37]">{info?.order_ref}</span> is booked. We'll email you shortly to arrange a time that suits you — and you'll get your FREE 7-day 'Clickable Video Ideas Plan' after the session.</p>
            <Link to="/" className="mt-6 inline-block text-sm text-white/50 hover:text-[#D4AF37]">Back to home</Link>
          </>
        )}
        {status === "paid" && info?.kind !== "consultation" && (
          <>
            <CheckCircle2 className="mx-auto text-[#D4AF37]" size={52} />
            <h1 className="mt-5 font-display text-3xl">Payment successful!</h1>
            <p className="mt-3 text-white/70">Thank you! Your order <span className="text-[#D4AF37]">{info?.order_ref}</span> is confirmed. We've emailed your receipt and next steps.</p>
            <div className="mt-8 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6">
              <div className="flex items-center justify-center gap-2 text-[#D4AF37]"><ClipboardList size={20} /><span className="font-display text-lg">One quick step</span></div>
              <p className="mt-2 text-sm text-white/70">Fill in your short questionnaire so we can make your plan perfectly for you.</p>
              {info?.questionnaire_token && (
                <GoldButton as="link" to={`/questionnaire/${info.questionnaire_token}`} data-testid="success-questionnaire-btn" className="mt-5">
                  Complete Your Questionnaire
                </GoldButton>
              )}
            </div>
            <Link to="/" className="mt-6 inline-block text-sm text-white/50 hover:text-[#D4AF37]">Back to home</Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <h1 className="font-display text-2xl">We couldn't confirm the payment</h1>
            <p className="mt-3 text-white/60 text-sm">If you were charged, don't worry — email us and we'll sort it out right away.</p>
            <GoldButton as="link" to="/content-plans" className="mt-6">Back to Content Plans</GoldButton>
          </>
        )}
      </div>
    </Section>
  );
}
